// TradeRow.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import FeedItem from '../feed/FeedItem';
import { onActivate } from '../../utils/external';
import {
  companyPath,
  findCompanyByTicker,
  formatDay,
  memberPath,
  tradeOwnerLabel,
  txTypeLabel,
} from '../../utils/spending';

/**
 * TradeRow
 *
 * One STOCK Act transaction as a member reported it, rendered as a FeedItem so
 * it sits in the Spending topic exactly like the feeds around it: same card,
 * Source button (the member's filed report), save and share.
 *
 *   title    the member and their seat; tapping it opens their record
 *   details  whose trade it was, what kind, the ticker; the asset; the range
 *            as reported, the day of the trade and how long after it was filed
 *
 * What the lines are careful about:
 *
 *   - The range, exactly as reported. Members report ranges, not amounts, and
 *     no midpoint is ever shown.
 *   - Whose trade it was: the member, their spouse, jointly, or a dependent
 *     child. It is the member's household's trade, never the company's.
 *   - Stock or option. The same trade can appear as both.
 *   - A report later than the 45 days the law allows is stated as a fact,
 *     since it may have a reason the record does not give.
 *   - The ticker opens the company when it is in the companies dataset,
 *     matched by exact ticker rather than name search, and does nothing
 *     visible otherwise rather than guessing.
 *
 * A former member has no bioguide id; their title opens the report instead.
 *
 * On a member's own page the member is already the subject, so showMember is
 * off: the title becomes the asset, and tapping it opens the report.
 */

const LAW_DAYS = 45;

function TradeRow({ trade, showMember = true }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const memberLink = memberPath(trade.bioguide_id, trade.chamber);
  const late = typeof trade.filed_after_days === 'number' && trade.filed_after_days > LAW_DAYS;

  const openTicker = async () => {
    if (!trade.ticker) return;
    try {
      const company = await findCompanyByTicker(trade.ticker);
      if (company?.org_id) navigate(companyPath(company.org_id));
    } catch {
      // Not in the dataset, or the lookup failed. Nothing to open.
    }
  };

  const item = {
    id: trade.url || `${trade.member_name}-${trade.tx_date}-${trade.ticker}`,
    title: showMember
      ? [trade.member_name, trade.state_district].filter(Boolean).join(', ')
      : trade.asset || trade.ticker || t('common:unnamedAsset', 'Unnamed asset'),
    created_at: trade.tx_date,
    url: trade.url,
  };

  const details = [
    <span className="trade-what">
      <span className="trade-owner">{tradeOwnerLabel(trade.owner, t)}</span>
      <span className="trade-type">{txTypeLabel(trade.tx_type, t)}</span>
      {trade.ticker && (
        <span
          className="trade-ticker"
          role="link"
          tabIndex={0}
          onClick={openTicker}
          onKeyDown={onActivate(openTicker)}
        >
          {trade.ticker}
        </span>
      )}
      {trade.asset_type === 'OP' && <span className="trade-tag">{t('common:option', 'Option')}</span>}
    </span>,
    showMember && trade.asset ? <span className="trade-asset">{trade.asset}</span> : null,
    <span className="trade-facts">
      <span className="trade-amount">{trade.amount}</span>
      {formatDay(trade.tx_date) && (
        <span>{t('common:tradedOn', 'traded {{date}}', { date: formatDay(trade.tx_date) })}</span>
      )}
      {typeof trade.filed_after_days === 'number' && (
        <span className={late ? 'trade-late' : ''}>
          {late
            ? t('common:reportedAfterLate', 'reported {{days}} days after, past the 45 the law allows', {
                days: trade.filed_after_days,
              })
            : t('common:reportedAfter', 'reported {{days}} days after', {
                days: trade.filed_after_days,
              })}
        </span>
      )}
    </span>,
  ];

  return (
    <FeedItem
      item={item}
      feedName={t('common:congressTrades', 'Congress stock trades')}
      branch="Spending"
      topic={t('common:congressTrades', 'Congress stock trades')}
      buttonName={null}
      schema=""
      table=""
      variant="default"
      details={details}
      shareUrl={trade.url}
      showDate={false}
      onTitleClick={showMember && memberLink ? () => navigate(memberLink) : null}
    />
  );
}

export default TradeRow;